import React from 'react';
import { motion } from 'framer-motion';
import {
    Megaphone, Target, BarChart3, Users, Mail, MessageSquare,
    Globe, Zap, ArrowRight, Share2, Facebook, Instagram, Twitter,
    Linkedin, Youtube, Radio, PenTool, Layout, ChevronLeft, HeartHandshake
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const DigitalMarketing = () => {
    const strategyItems = [
        { title: "Social Media", desc: "Building brand presence where your audience hangs out.", icon: Share2 },
        { title: "Content", desc: "Attract and retain customers with valuable storytelling.", icon: PenTool },
        { title: "Email", desc: "Targeted campaigns that drive direct conversions.", icon: Mail },
        { title: "PPC", desc: "Instant visibility through strategic paid advertising.", icon: Target },
        { title: "Influencer", desc: "Leverage trusted voices to amplify your message.", icon: Users },
        { title: "Analytics", desc: "Data-driven insights to optimize every campaign.", icon: BarChart3 }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Digital Marketing | Grow Your Brand & ROI - MehareTech"
                description="Scale your business with data-driven digital marketing. Specialized in Social Media Marketing, Performance PPC, Strategic Content, and Conversion-focused Email campaigns."
                keywords="digital marketing agency India, social media marketing, PPC management service, content marketing strategy, email marketing automation, digital brand growth, online advertising"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-pink-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-pink-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-pink-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Global Growth
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            SCALE WITH <span className="text-gradient-pink">DIGITAL PERFORMANCE</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We turn browsers into buyers through data-driven digital storytelling.
                        </p>
                        <button className="px-8 py-3 bg-pink-600 text-white font-bold rounded-xl hover:bg-pink-700 transition-all flex items-center gap-2 mx-auto text-sm">
                            Start Growing <ArrowRight size={18} />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Stats & Services Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                        {[
                            { label: "ROI Focused", val: "300%+", icon: BarChart3 },
                            { label: "Ad Management", val: "$1M+", icon: Target },
                            { label: "Growth Partner", val: "500+", icon: HeartHandshake }
                        ].map((stat, i) => (
                            <div key={i} className="p-6 bg-white/5 border border-white/5 rounded-2xl text-center">
                                <stat.icon size={24} className="text-pink-500 mx-auto mb-2" />
                                <h3 className="text-2xl font-black">{stat.val}</h3>
                                <p className="text-slate-500 font-bold uppercase tracking-widest text-[9px]">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {strategyItems.map((item, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-pink-500/30 transition-all group"
                            >
                                <item.icon size={24} className="text-pink-500 mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Visual Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 leading-tight">
                                Data-Driven, <span className="text-pink-500">Creative</span>
                            </h2>
                            <ul className="space-y-3">
                                {['Omnichannel Strategy', 'Real-time Dashboards', 'Creative Ad Design', 'Conversion Optimization'].map((li, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                                        <Zap size={14} className="text-pink-500" />
                                        {li}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="md:w-1/2 relative">
                            <img
                                src="https://images.pexels.com/photos/905163/pexels-photo-905163.jpeg"
                                alt="Marketing"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DigitalMarketing;
